/**
 * Pot Odds quiz route.
 *
 * Renders question-specific UI (pot/bet display, equity-percent buttons,
 * reveal rationale). Page chrome + session state + persist all live in
 * the shared `QuizLayout` / `useQuizSession` / `RevealPanel` primitives.
 */

import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { Stat } from "@/features/quiz/components/Stat";
import { Brain } from "@/shared/components/icons";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  describeFraction,
  equityNeeded,
  formulaLine,
  generateQuestion,
  type PotOddsQuestion,
} from "@/features/quiz/lib/potOddsQuiz";

export function QuizPotOdds() {
  const session = useQuizSession<PotOddsQuestion, number>({
    quizType: "pot_odds",
    generate: generateQuestion,
    getCorrectAnswer: (q) => q.options[q.correctIndex]!,
    getQuestionPayload: (q) => ({
      pot: q.pot,
      bet: q.bet,
      bet_fraction: q.betFraction,
    }),
  });
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } = session;
  const exactEquity = equityNeeded(question.pot, question.bet);

  return (
    <QuizLayout
      Icon={Brain}
      title="Pot Odds"
      subtitle="Bet sizing → equity needed: sheet 5"
      description="Tests the equity you need to call profitably given the pot and villain's bet. The single most fundamental call/fold math: recognizing canonical bet sizes (half-pot, two-thirds, pot-sized) and the equity each requires is what separates a thinking caller from a guesser."
      stats={stats}
    >
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="flex items-baseline gap-8">
          <Stat label="POT" value={`${question.pot}bb`} />
          <Stat label="VILLAIN BETS" value={`${question.bet}bb`} accent />
        </div>
      </div>

      <p className="text-center text-text-muted text-sm">
        What equity do you need to call?
      </p>

      <div className="grid grid-cols-2 gap-3">
        {question.options.map((eq) => (
          <QuizOption
            key={eq}
            value={eq}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="text-lg">{eq}%</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{correctAnswer}%</strong>
            </>
          }
          rationale={
            <>
              <span className="font-mono text-text">
                {formulaLine(question.pot, question.bet, Math.round(exactEquity * 10) / 10)}
              </span>
              <br />A {describeFraction(question.betFraction)} requires ~{correctAnswer}% equity to call profitably.
            </>
          }
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}

